/**
 * Supabase Client Configuration & Singleton Instance.
 *
 * Configured for Aster Decorz E-Commerce.
 * Authentication supports Email & Password, Magic Link, and Phone SMS OTP.
 * SSR-safe & WebSocket polyfilled across Node.js 20+ environments.
 */

import { createClient } from "@supabase/supabase-js";
import WebSocket from "ws";

export const SUPABASE_URL = (import.meta.env.VITE_SUPABASE_URL as string) || "";

export const SUPABASE_ANON_KEY = (import.meta.env.VITE_SUPABASE_ANON_KEY as string) || "";

export const isSupabaseConfigured = Boolean(SUPABASE_URL && SUPABASE_ANON_KEY);

/**
 * Resolves image paths for local dev viewing and Supabase CDN deployment.
 */
export function getCdnUrl(path: string): string {
  if (!path) return "";
  if (path.startsWith("http://") || path.startsWith("https://")) {
    return path;
  }
  // Return local path directly for dev photo viewing
  if (path.startsWith("/")) {
    return path;
  }
  return `/${path.replace(/^\/?(plantpicture|plant%20picture)\//, "plantpicture/")}`;
}

const isBrowser = typeof window !== "undefined";

// Polyfill WebSocket in Node.js < 22 SSR environments
if (!isBrowser && typeof globalThis.WebSocket === "undefined") {
  globalThis.WebSocket = WebSocket as unknown as typeof globalThis.WebSocket;
}

export const supabase = createClient(
  SUPABASE_URL || "https://unconfigured.supabase.co",
  SUPABASE_ANON_KEY || "unconfigured-key",
  {
    auth: {
      persistSession: isBrowser,
      autoRefreshToken: isBrowser,
      detectSessionInUrl: isBrowser,
      storageKey: "verdant_auth_token",
    },
    ...(isBrowser
      ? {}
      : {
          realtime: {
            transport: WebSocket as unknown as typeof globalThis.WebSocket,
          },
        }),
  },
);
